<template>
  <div class="article">
    <div :style="bgImage" class="article-header">
      <div class="title">
        {{ article.title }}
        <a
          @click.prevent="$emit('closeArticle')"
          :href="`/${$route.path.split('/').splice(-2, 1)}`"
          class="go-back"
        >
          <Arrow
            :size="$mq === 'sm' ? 8 : 14"
            class="arrow arrow_left arrow_color_contrast"
          />
          <p v-if="$mq === 'sm'">{{ $t('articles.article.header.back') }}</p>
        </a>
        <div class="date">
          {{ $t('articles.article.header.updated') }}: {{ articleDate }}
        </div>
      </div>
    </div>
    <p
      v-if="($mq === 'sm' || $mq === 'md') && hasTickerInfo"
      @click="infoShown = !infoShown"
      class="switch"
    >
      {{
        infoShown
          ? $t('articles.article.switch.backToArticle')
          : $t('articles.article.switch.view')
      }}
    </p>
    <div class="article-content">
      <div v-if="showArticleContent" class="text-wrapper m-0 p-0">
        <div v-html="changedHtml" class="text"></div>
        <buy-key v-if="article.pro && !article.bought" :shadow="true">
          <h3 class="text">{{ $t('articles.article.buy.title') }}</h3>
          <button class="buy" @click="$emit('buy', article)">
            {{ article.price }} {{ article.currency }}
          </button>
        </buy-key>
      </div>
      <div v-if="showInfoBlock" class="information">
        <h3 class="ticker-name" @click="goToDashboard">
          {{ article.ticker_market_infos[0].name }}
        </h3>
        <div v-if="this.$store.state.market.currentPrice" class="price">
          <p class="type">{{ $t('articles.article.info.price.current') }}:</p>
          <p
            class="number"
            :class="[
              !$store.state.market.trading
                ? 'text-black'
                : currentChange > 0
                ? 'text-primary'
                : currentChange < 0
                ? 'text-red'
                : 'text-black',
              $mq === 'sm' && largeNum ? 'small-text' : '',
            ]"
          >
            {{ $store.state.market.currentPrice | financial }}
            {{ article.ticker_market_infos[0].currency }}
          </p>
        </div>
        <div
          v-if="article.ticker_market_infos[0].fundamentalPrice"
          class="price"
        >
          <p class="type">
            {{ $t('articles.article.info.price.fundamental') }}:
          </p>
          <div
            class="indicators"
            :class="[$mq === 'sm' && largeNum ? 'small-text' : '']"
          >
            <p
              class="number"
              :class="[
                changeFundPercent > 0
                  ? 'text-primary'
                  : changeFundPercent < 0
                  ? 'text-red'
                  : 'text-black',
              ]"
            >
              {{ changeFundPercent | percent }}
            </p>
            <p
              class="number"
              :class="[
                changeFundPercent > 0
                  ? 'text-primary'
                  : changeFundPercent < 0
                  ? 'text-red'
                  : 'text-black',
              ]"
            >
              {{ article.ticker_market_infos[0].fundamentalPrice }}
              {{ article.ticker_market_infos[0].currency }}
            </p>
          </div>
        </div>
        <div
          v-if="
            // if article isn't pro
            (!article.pro && article.target) ||
              // if article is pro
              (article.pro &&
                // true, if not bought or if bought and target isn't empty
                (!article.bought || (article.bought && article.target)))
          "
          class="price"
        >
          <p class="type">{{ $t('articles.article.info.price.target') }}:</p>
          <div
            class="indicators"
            :class="[$mq === 'sm' && largeNum ? 'small-text' : '']"
          >
            <p
              v-if="this.$store.state.market.currentPrice"
              class="number"
              :class="[
                profit > 0
                  ? 'text-primary'
                  : profit < 0
                  ? 'text-red'
                  : 'text-black',
                article.pro && !article.bought ? 'blur' : '',
              ]"
            >
              {{ article.target ? addPers(profit) : '0000' }}
            </p>
            <p
              class="number"
              :class="[
                profit > 0
                  ? 'text-primary'
                  : profit < 0
                  ? 'text-red'
                  : 'text-black',
                article.pro && !article.bought ? 'blur' : '',
              ]"
            >
              {{ article.target ? article.target : '00000' }}
              {{ article.ticker_market_infos[0].currency }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import injectInitialState from '@/utils/inject-initial-state'
import filters from '@/utils/filters'
import Arrow from '@/components/Calendar/CalendarSoritng/Arrow.vue'
import buyKey from '@/components/library/buyKey'

const md = require('markdown-it')({
  html: true,
  linkify: true,
  typographer: true,
})
const BACKOFFICE = process.env.VUE_APP_BACKOFFICE_URL || 'http://localhost:82/'

export default {
  props: {
    article: Object,
  },
  data() {
    return {
      infoShown: false,
      market:
        this.article.ticker_market_infos && this.article.ticker_market_infos[0]
          ? this.article.ticker_market_infos[0].name.split(':')[0]
          : '',
      ticker:
        this.article.ticker_market_infos && this.article.ticker_market_infos[0]
          ? this.article.ticker_market_infos[0].name.split(':')[1]
          : '',
    }
  },
  mounted() {
    this.loadData()
  },
  computed: {
    articleDate() {
      const date = new Date(
        this.article.updated_at
          ? this.article.updated_at
          : this.article.published_at
      )
      return new Intl.DateTimeFormat(this.$i18n.locale).format(date)
    },
    showInfoBlock() {
      return (
        this.hasTickerInfo && // only if has ticker info
        ((this.$mq !== 'sm' && this.$mq !== 'md') || // if resolutions are not sm and md
          this.infoShown) // or resolutions are sm or md, but info is enabled
      )
    },
    showArticleContent() {
      return !(
        this.infoShown && // if not (ticker info is shown and resolution is sm or md)
        (this.$mq === 'sm' || this.$mq === 'md')
      )
    },
    changedHtml() {
      let selectedArticle = this.article.content
      if (!selectedArticle) return ''
      const content = selectedArticle
        .replace(/]\(\/uploads/gi, `](${BACKOFFICE}uploads`)
        .replace(/src="\/uploads/gi, `src="${BACKOFFICE}uploads`)
      return md.render(content)
    },
    bgImage() {
      return `background-image: linear-gradient(360deg, #000000 0%, rgba(0, 0, 0, 0) ${
        this.$mq === 'sm' ? '60%' : '40%'
      }), url(${BACKOFFICE + this.article.image.url.slice(1)});`
    },
    hasTickerInfo() {
      return (
        !!this.article.ticker_market_infos &&
        !!this.article.ticker_market_infos[0] &&
        !!this.$store.state.market.currentPrice
      )
    },
    currentChange() {
      return (
        this.$store.state.market.currentPrice -
        this.$store.state.market.lastPrice
      )
    },
    profit() {
      return Math.round(
        (this.article.target / this.$store.state.market.currentPrice) * 100 -
          100
      )
    },
    largeNum() {
      return (
        (this.article.ticker_market_infos[0]
          ? this.article.ticker_market_infos[0].fundamentalPrice.toString()
              .length > 5
          : false) ||
        this.$store.state.market.currentPrice.toString().length > 5 ||
        (this.article.target
          ? this.article.target.toString().length > 5
          : false)
      )
    },
    ...mapGetters({
      change: 'market/change',
      changePercent: 'market/changePercent',
      changeFundPercent: 'market/changeFundPercent',
    }),
  },
  methods: {
    addPers(num) {
      return (num > 0 ? '+' : num === 0 ? '' : '-') + num + '%'
    },
    goToDashboard() {
      this.$router.push(`/dashboard/${this.ticker}/${this.market}`)
    },
    loadData() {
      if (this.ticker && this.market) {
        this.$store.commit('market/updateTicker', this.ticker)
        this.$store.commit('market/updateMarket', this.market)
        Promise.all([
          this.$store.dispatch('layout/updateAll'),
          ...[
            !this.$store.state.request.socket.ready &&
              this.$store.dispatch('request/socket/init'),
          ],
          this.$store.dispatch('request/socket/subscribe', {
            ticker: this.$store.state.market.ticker,
          }),
        ]).finally(() => {
          if (window.__PRERENDER_INJECTED) {
            const render = () => {
              injectInitialState(this.$store.state)
              document.dispatchEvent(new Event('prerender-now'))
            }
            setTimeout(render, 10000) //fallback
            const intID = setInterval(() => {
              if (this.$store.state.market.companyName != 'loading') {
                render()
                clearInterval(intID)
              }
            }, 100)
          }
        })
      }
    },
  },
  filters: {
    percent: filters.percentWithPlus,
    financial: filters.financial,
  },
  components: {
    Arrow,
    'buy-key': buyKey,
  },
}
</script>

<style lang="scss">
.article {
  margin: 0 auto;
  color: $title-color;
  max-width: 1400px;
  .go-back {
    cursor: pointer;
    p {
      color: $title-color;
      margin: 1px 0;
    }
  }
  > .article-header {
    position: relative;
    min-height: 400px;
    height: calc(66vh - 60px);
    width: 100%;
    overflow: hidden;
    display: flex;

    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;

    > .title {
      position: absolute;
      bottom: 0;
      z-index: 20;
      margin: 0 116px;
      font-weight: bold;
      font-size: 32px;
      line-height: 40px;
      color: white;
      > .go-back {
        position: absolute;
        top: 2px;
        left: -46px;
      }
      > .date {
        font-weight: normal;
        font-size: 16px;
        line-height: 24px;
        color: #8a8a8a;
        margin: 8px 0px;
      }
    }

    > .image {
      margin: 0;
      height: 100%;
      width: 100%;
      object-fit: cover;
      z-index: 0;
    }
    > .gradient {
      height: 100%;
      width: 100%;
      position: absolute;
      bottom: 0;
      background: linear-gradient(360deg, #000000 0%, rgba(0, 0, 0, 0) 41%);
      z-index: 10;
    }

    @media (max-width: 991px) {
      height: calc(50vh - 60px);
      justify-content: center;
      > .title {
        font-size: 24px;
        margin: 0;
        min-width: calc(100% - 80px);
        left: 50%;
        transform: translate(-50%, 0);
        bottom: 0;
        > .go-back {
          left: -38px;
        }
      }
    }
    @media (max-width: 767px) {
      > .title {
        margin: 0 10px;
        font-size: 20px;
        line-height: 28px;

        > .date {
          font-size: 12px;
          line-height: 20px;
        }
      }
    }
    @media (max-width: 576px) {
      height: 220px;
      min-height: auto;
      > .title {
        width: calc(100% - 32px);
        margin: 0;
        font-size: 16px;
        line-height: 20px;
        top: auto;
        bottom: 0;
        transform: translate(-50%, 0);
        > .go-back {
          left: -8px;
          top: -22px;
          > p {
            display: inline-block;
            font-size: 12px;
            line-height: 16px;
            color: #fff;
            margin: 0;
            margin-top: 3px;
          }
          > .arrow {
            bottom: 0;
          }
        }
      }
    }
  }
  > .switch {
    font-weight: bold;
    font-size: 12px;
    line-height: 24px;
    color: #ff6e51;
    margin: 12px auto 0;
    max-width: 558px;

    @media (max-width: 576px) {
      margin: 12px 16px 0;
    }
  }
  > .article-content {
    margin: 36px 16px;
    display: flex;
    align-items: flex-start;
    justify-content: space-around;
    gap: 40px;

    > .text-wrapper {
      > .text {
        max-width: 558px;
        width: 558px;
        margin: 0;
        img {
          max-width: 100%;
          margin: 10px auto -2px;
        }

        ol,
        ul {
          margin: 0;
          margin-block-start: 0.6em;
          margin-block-end: 0.6em;
        }

        li > ol:first-child,
        li > ul:first-child {
          margin-block-start: 0.6em;
        }

        ul > li {
          list-style: disc;
        }

        ul.to-do-list {
          text-indent: -1.7em;
        }

        ul {
          padding-inline-start: 1.7em;
        }

        ul > li {
          padding-left: 0.1em;
          line-height: 25px;
          margin-top: 0px;
          margin-bottom: 6px;
          word-spacing: -0.6px;
          letter-spacing: -0.6px;
        }

        ol {
          padding-inline-start: 1.6em;
        }

        ol > li {
          padding-left: 0.2em;
        }

        h1,
        h2,
        h3 {
          letter-spacing: -0.01em;
          line-height: 1.2;
          font-weight: 600;
          margin-bottom: 0;
        }

        img.icon {
          border-radius: 3px;
        }

        blockquote {
          font-size: 1.25em;
          margin: 1em 0;
          padding-left: 1em;
          border-left: 3px solid rgb(55, 53, 47);
          color: $text-color;
        }

        h1 {
          margin-top: 42px;
          font-size: 29px;
          letter-spacing: -0.6px;
          word-spacing: -1px;
          margin-bottom: 13px;
        }

        h2 {
          font-size: 22px;
          word-spacing: 1px;
          letter-spacing: 0.2px;
          margin-top: 32px;
          margin-bottom: 10px;
        }

        h3 {
          font-size: 18px;
          word-spacing: 1px;
          letter-spacing: 0.3px;
          margin-top: 26px;
          margin-bottom: 6px;
        }

        p {
          margin: 8px auto;
          line-height: 24px;
          font-size: 16px;
          letter-spacing: -0.9px;
          word-spacing: 0.8px;
        }

        figure {
          margin: 1.25em 0;
          page-break-inside: avoid;
        }

        figcaption {
          opacity: 0.5;
          font-size: 85%;
          margin-top: 0.5em;
        }

        hr {
          background: transparent;
          display: block;
          width: 100%;
          height: 1px;
          visibility: visible;
          border: none;
          border-bottom: 1px solid rgba(55, 53, 47, 0.09);
        }
      }

      > .not-pro {
        > .text {
          margin: 0 auto;
        }
        > .buy {
          width: 159px;
          height: 24px;
          background: $primary-color2;
          border-radius: 4px;
          border: none;
          color: #fff;
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 0 auto;
        }
      }
    }
    > .information {
      padding: 22px;
      min-width: 280px;
      width: 440px;
      border: 1px solid #c2c2d6;
      box-sizing: border-box;
      border-radius: 8px;
      position: sticky;
      top: 36px;
      > .ticker-name {
        font-size: 16px;
        line-height: 24px;
        margin-bottom: 12px;
        cursor: pointer;
        transition: all 0.5s ease-in-out;
        &:hover {
          color: $primary-color2;
        }
      }
      > .price {
        display: flex;
        justify-content: space-between;
        align-items: center;
        > .type {
          margin: 0;
          font-size: 12px;
          line-height: 16px;
        }
        > .indicators,
        & {
          > .number {
            margin: 0;
            font-weight: bold;
            transition: all 0.5s ease-in-out;
            .text-black {
              color: #000 !important;
            }
            &.blur {
              filter: blur(8px);
            }
            &.small-text,
            > .small-text {
              font-size: 12px;
            }
          }
        }
        > .indicators {
          width: 40%;
          display: flex;
          justify-content: space-between;
          &.small-text,
          > .small-text {
            font-size: 12px;
          }
        }
      }
    }
    @media (max-width: 991px) {
      margin-top: 12px;
      > .text-wrapper {
        .text {
          width: auto;
        }
      }
    }
    @media (max-width: 576px) {
      > .text-wrapper {
        .text {
          h1 {
            font-size: 20px;
            line-height: 24px;
          }
          h2 {
            font-size: 16px;
            line-height: 24px;
          }
          &,
          h3,
          p,
          ul > li {
            font-size: 12px;
            line-height: 18px;
          }
        }
      }
      > .information {
        width: 100%;
        border: none;
        padding: 0;
      }
    }
  }
}

#app[data-theme='dark'] {
  .article {
    .go-back {
      p {
        color: #fff;
      }
    }
    a {
      color: $primary-color2;
    }
    color: #fff;
    .article-content {
      &,
      h1,
      h2,
      h3,
      p,
      ul > li {
        color: #d6d6d6 !important;
      }
      .text-primary {
        color: #08b64d !important;
      }
      .text-red {
        color: #ed5051 !important;
      }

      .not-pro .clear-shadow {
        background: linear-gradient(rgba(255, 255, 255, 0), $title-color);
      }
    }
  }
}
</style>
